import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, IconButton, Button, TextField, Drawer } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
//import TouchAppIcon from '@mui/icons-material/TouchApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestoreIcon from '@mui/icons-material/Restore';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { useNotifications } from '../../hooks/useNotifications';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useRequestQueue } from '../../hooks/useRequestQueue';
import { getShoppingList, changeProductStatus, updateShoppingList, copyShoppingList } from '../../api/shoppingList';
import { ShoppingListWithProducts, ShoppingListProduct } from '../../types/shoppingList';
import { t } from '../../utils/translations';
import { styles } from './ShoppingListView.styles';
import AddProductPanel from './AddProductPanel';
import EditProductPanel from './EditProductPanel';
import AddMultipleProductsDialog from './AddMultipleProductsDialog';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

interface ProductsByCategory {
  [key: string]: ShoppingListProduct[];
}

const ShoppingListView: React.FC = () => {
  const { shoppingListId } = useParams<{ shoppingListId: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess, showInfo } = useNotifications();
  const isOnline = useOnlineStatus();
  const [loading, setLoading] = useState<boolean>(true);
  const [list, setList] = useState<ShoppingListWithProducts | null>(null);
  const [productsByCategory, setProductsByCategory] = useState<ProductsByCategory>({});
  const [addPanelOpen, setAddPanelOpen] = useState<boolean>(false);
  const [addMultiplePanelOpen, setAddMultiplePanelOpen] = useState<boolean>(false);
  const [editPanelOpen, setEditPanelOpen] = useState<boolean>(false);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>('');
  const [editingProduct, setEditingProduct] = useState<ShoppingListProduct | null>(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState<boolean>(false);
  const [copyListName, setCopyListName] = useState<string>('');
  const [copying, setCopying] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [gestureMode/*, setGestureMode*/] = useState<boolean>(false);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

  const fetchList = async (isAutoRefresh = false) => {
    if (!shoppingListId) return;

    try {
      if (!isAutoRefresh) {
        setLoading(true);
      }
      const data = await getShoppingList(shoppingListId);
      setList(data);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('shoppingLists.loadFailed'),
        t('common.error')
      );
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  };

  const handleQueueProcessed = async () => {
    // Refresh list after queue is processed
    await fetchList(true);
  };

  const { executeOrQueue } = useRequestQueue(handleQueueProcessed);

  useEffect(() => {
    // Initial fetch - only if online
    if (isOnline) {
      fetchList();
    }

    // Set up auto-refresh every 20 seconds
    const refreshInterval = setInterval(() => {
      if (isOnline) {
        fetchList(true);
      }
    }, 20000);

    // Cleanup interval on unmount
    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shoppingListId, isOnline]);

  useEffect(() => {
    if (list) {
      organizeProductsByCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  useEffect(() => {
    const handlePopState = () => {
      if (editPanelOpen) {
        setEditPanelOpen(false);
      } else if (addPanelOpen) {
        setAddPanelOpen(false);
      } else if (addMultiplePanelOpen) {
        setAddMultiplePanelOpen(false);
      } else if (copyDialogOpen) {
        setCopyDialogOpen(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [addPanelOpen, addMultiplePanelOpen, editPanelOpen, copyDialogOpen]);

  const organizeProductsByCategory = () => {
    if (!list) return;

    const organized: ProductsByCategory = {};
    const checkedOff: ShoppingListProduct[] = [];
    const isTemplate = list.status === 10;

    list.products.forEach((product) => {
      // For templates, all products stay in their original categories
      // For non-templates, products with status 1 (bought) or 2 (unavailable) go to "checked off" category
      if (!isTemplate && (product.status === 1 || product.status === 2)) {
        checkedOff.push(product);
      } else {
        const categoryKey = product.category || 'none';
        if (!organized[categoryKey]) {
          organized[categoryKey] = [];
        }
        organized[categoryKey].push(product);
      }
    });

    // Add checked off products as a separate virtual category (only for non-templates)
    if (!isTemplate && checkedOff.length > 0) {
      organized['__checkedOff'] = checkedOff;
    }

    setProductsByCategory(organized);
  };

  const handleStatusChange = async (productId: string, newStatus: 0 | 1 | 2) => {
    if (!shoppingListId || !list) return;

    // Optimistically update the UI immediately
    const updatedProducts = list.products.map((p) =>
      p.productId === productId ? { ...p, status: newStatus } : p
    );
    setList({ ...list, products: updatedProducts });

    try {
      // Execute request or queue it if offline
      const result = await executeOrQueue(
        () => changeProductStatus(shoppingListId, productId, { status: newStatus }),
        {
          type: 'status-change',
          shoppingListId,
          payload: { productId, status: newStatus },
        }
      );

      // If request was queued (offline), show info message
      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      }
    } catch (error) {
      // On error, refresh the list to restore the correct state
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );

      try {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
      } catch (refreshError) {
        // If refresh also fails, just log it
        console.error('Failed to refresh list after status change error:', refreshError);
      }
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!shoppingListId || !list) return;

    const remainingProducts = list.products
      .filter((p) => p.productId !== productId)
      .map((p) => ({
        productId: p.productId,
        quantity: p.quantity,
        comment: p.comment || undefined,
      }));

    // Optimistically update UI
    const updatedProductsList = list.products.filter((p) => p.productId !== productId);
    setList({ ...list, products: updatedProductsList });

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { products: remainingProducts }),
        {
          type: 'product-delete',
          shoppingListId,
          payload: { products: remainingProducts },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      } else if (result !== null) {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
      // Restore previous state on error
      const updatedList = await getShoppingList(shoppingListId);
      setList(updatedList);
    }
  };

  const handleAddProduct = async (productId: string, quantity: number, comment?: string) => {
    if (!shoppingListId || !list) return;

    const existingProducts = list.products.map((p) => ({
      productId: p.productId,
      quantity: p.quantity,
      comment: p.comment || undefined,
    }));

    const newProduct = {
      productId,
      quantity,
      comment,
    };

    const updatedProducts = [...existingProducts, newProduct];

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { products: updatedProducts }),
        {
          type: 'product-add',
          shoppingListId,
          payload: { products: updatedProducts },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      }

      const updatedList = await getShoppingList(shoppingListId);
      setList(updatedList);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleCompleteList = async () => {
    if (!shoppingListId) return;

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { status: 2 }),
        {
          type: 'list-update',
          shoppingListId,
          payload: { status: 2 },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      } else {
        // Show success message and navigate to home page
        showSuccess(t('shoppingLists.listCompleted'), t('common.success'));
        navigate('/');
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleReopenList = async () => {
    if (!shoppingListId) return;

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { status: 1 }),
        {
          type: 'list-update',
          shoppingListId,
          payload: { status: 1 },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      } else {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleConvertToTemplate = async () => {
    if (!shoppingListId) return;

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { status: 10 }),
        {
          type: 'list-update',
          shoppingListId,
          payload: { status: 10 },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      } else {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleRemoveTemplate = async () => {
    if (!shoppingListId) return;

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { status: 2 }),
        {
          type: 'list-update',
          shoppingListId,
          payload: { status: 2 },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      } else {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleStartEditingName = () => {
    if (list && (list.status === 1 || list.status === 10)) {
      setEditedName(list.name);
      setIsEditingName(true);
    }
  };

  const handleSaveListName = async () => {
    if (!shoppingListId || !editedName.trim()) return;

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { name: editedName.trim() }),
        {
          type: 'list-update',
          shoppingListId,
          payload: { name: editedName.trim() },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
        setIsEditingName(false);
      } else {
        const updatedList = await getShoppingList(shoppingListId);
        setList(updatedList);
        setIsEditingName(false);
      }
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const handleOpenEditPanel = (product: ShoppingListProduct) => {
    setEditingProduct(product);
    window.history.pushState({ modalOpen: true }, '');
    setEditPanelOpen(true);
  };

  const handleCloseEditPanel = () => {
    if (editPanelOpen) {
      window.history.back();
    } else {
      setEditPanelOpen(false);
    }
    setEditingProduct(null);
  };

  const handleSaveProductEdit = async (quantity: number, comment?: string) => {
    if (!shoppingListId || !list || !editingProduct) return;

    const updatedProducts = list.products.map((p) =>
      p.productId === editingProduct.productId
        ? {
            productId: p.productId,
            quantity,
            comment: comment || undefined,
          }
        : {
            productId: p.productId,
            quantity: p.quantity,
            comment: p.comment || undefined,
          }
    );

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { products: updatedProducts }),
        {
          type: 'product-edit',
          shoppingListId,
          payload: { products: updatedProducts },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      }

      const updatedList = await getShoppingList(shoppingListId);
      setList(updatedList);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
      throw error;
    }
  };

  const handleOpenAddPanel = () => {
    window.history.pushState({ modalOpen: true }, '');
    setAddPanelOpen(true);
  };

  const handleCloseAddPanel = () => {
    if (addPanelOpen) {
      window.history.back();
    } else {
      setAddPanelOpen(false);
    }
  };

  const handleOpenAddMultiplePanel = () => {
    window.history.pushState({ modalOpen: true }, '');
    setAddMultiplePanelOpen(true);
  };

  const handleCloseAddMultiplePanel = () => {
    if (addMultiplePanelOpen) {
      window.history.back();
    } else {
      setAddMultiplePanelOpen(false);
    }
  };

  const handleAddMultipleProducts = async (productIds: string[]) => {
    if (!shoppingListId || !list || productIds.length === 0) return;

    const existingProducts = list.products.map((p) => ({
      productId: p.productId,
      quantity: p.quantity,
      comment: p.comment || undefined,
    }));

    const newProducts = productIds.map((productId) => ({
      productId,
      quantity: 1,
      comment: undefined,
    }));

    const updatedProducts = [...existingProducts, ...newProducts];

    try {
      const result = await executeOrQueue(
        () => updateShoppingList(shoppingListId, { products: updatedProducts }),
        {
          type: 'product-add-multiple',
          shoppingListId,
          payload: { products: updatedProducts },
        }
      );

      if (result === null && !isOnline) {
        showInfo(t('shoppingLists.changeQueuedForLater'), t('common.offline'));
      }

      const updatedList = await getShoppingList(shoppingListId);
      setList(updatedList);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    }
  };

  const getDefaultListName = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `Lista na ${day}-${month}-${year}`;
  };

  const handleOpenCopyDialog = () => {
    setCopyListName(getDefaultListName());
    window.history.pushState({ modalOpen: true }, '');
    setCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    if (copyDialogOpen) {
      window.history.back();
    }
    setCopyListName('');
    setCopyDialogOpen(false);
  };

  const handleCopyList = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!shoppingListId || !copyListName.trim()) {
      showError('Nazwa listy jest wymagana', 'Błąd walidacji');
      return;
    }

    try {
      setCopying(true);
      const response = await copyShoppingList(shoppingListId, copyListName.trim());

      // Close dialog without going back in history, since we're navigating
      setCopyListName('');
      setCopyDialogOpen(false);

      // Navigate to the new list
      navigate(`/shopping-lists/${response.id}`);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : 'Nie udało się skopiować listy',
        'Błąd'
      );
    } finally {
      setCopying(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!shoppingListId) return;

    try {
      setDeleting(true);
      setDeleteDialogOpen(false);
      await updateShoppingList(shoppingListId, {
        status: -1,
      });

      // Show success message and navigate to home page
      showSuccess(t('shoppingLists.listDeleted'), t('common.success'));
      navigate('/');
    } catch (error) {
      showError(
        error instanceof Error ? error.message : t('common.error'),
        t('common.error')
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleTouchStart = (e: React.TouchEvent, product: ShoppingListProduct) => {
    if (!gestureMode || list?.status === 2 || list?.status === 10) return;

    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY, time: Date.now() });

    // Start long press timer (500ms)
    const timer = setTimeout(() => {
      handleOpenEditPanel(product);
      setTouchStart(null);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleTouchMove = () => {
    // Cancel long press on move
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent, product: ShoppingListProduct) => {
    if (!gestureMode || list?.status === 2 || list?.status === 10 || !touchStart) {
      setTouchStart(null);
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      return;
    }

    // Cancel long press timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const deltaTime = Date.now() - touchStart.time;

    // Check if it's a swipe (horizontal movement > vertical movement and fast enough)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && deltaTime < 300) {
      if (deltaX > 0) {
        // Swipe right - same as click (toggle status)
        const isBought = product.status === 1;
        const isUnavailable = product.status === 2;
        handleStatusChange(product.productId, (isBought || isUnavailable) ? 0 : 1);
      } else {
        // Swipe left - mark as unavailable (status 2)
        handleStatusChange(product.productId, 2);
      }
    }

    setTouchStart(null);
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!list) {
    return (
      <Box sx={styles.loadingContainer}>
        <Typography variant="h6">{t('shoppingLists.loadFailed')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.listPaper}>
        {isEditingName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <IconButton onClick={handleSaveListName} color="primary">
              <CheckIcon />
            </IconButton>
          </Box>
        ) : (
          <Typography
            variant="h5"
            sx={{
              ...styles.title,
              cursor: (list.status === 1 || list.status === 10) ? 'pointer' : 'default',
              mb: 2,
            }}
            onClick={handleStartEditingName}
          >
            {list.name}
          </Typography>
        )}

        {list.products.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={styles.emptyText}>
            {t('shoppingLists.emptyList')}
          </Typography>
        ) : (
          (() => {
            const categories = Object.keys(productsByCategory);
            const regularCategories = categories.filter(cat => cat !== '__checkedOff');
            const hasCheckedOff = categories.includes('__checkedOff');

            return [
              ...regularCategories.sort((a, b) =>
                t(`categories.${a}`).localeCompare(t(`categories.${b}`), 'pl')
              ),
              ...(hasCheckedOff ? ['__checkedOff'] : [])
            ];
          })().map((categoryKey) => {
              const categoryProducts = productsByCategory[categoryKey];

              if (categoryProducts.length === 0) {
                return null;
              }

              const isCheckedOffCategory = categoryKey === '__checkedOff';
              const categoryLabel = isCheckedOffCategory
                ? t('shoppingLists.checkedOff')
                : t(`categories.${categoryKey}`);

              return (
                <Box key={categoryKey} sx={styles.categorySection}>
                  <Typography variant="h6" sx={styles.categoryTitle}>
                    {categoryLabel}
                  </Typography>
                  {categoryProducts.map((product: ShoppingListProduct) => {
                    const isBought = product.status === 1;
                    const isUnavailable = product.status === 2;
                    const isListClosed = list.status === 2;
                    const isTemplate = list.status === 10;

                    const getProductColor = () => {
                      if (isTemplate) return 'text.primary';
                      if (isBought) return 'success.main';
                      if (isUnavailable) return 'error.main';
                      return 'text.primary';
                    };

                    return (
                      <Box key={product.productId} sx={styles.productItem}>
                        <Box
                          sx={{
                            ...styles.productInfo,
                            cursor: (isListClosed || isTemplate) ? 'default' : 'pointer',
                          }}
                          onClick={() => !gestureMode && !isListClosed && !isTemplate && handleStatusChange(product.productId, (isBought || isUnavailable) ? 0 : 1)}
                          onTouchStart={(e) => handleTouchStart(e, product)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={(e) => handleTouchEnd(e, product)}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              ...styles.productName,
                              textDecoration: (isTemplate || (!isBought && !isUnavailable)) ? 'none' : 'line-through',
                              color: getProductColor(),
                            }}
                          >
                            {product.quantity} {t(`units.${product.unit.toLowerCase()}`)} {product.name}
                          </Typography>
                          {product.comment && (
                            <Typography variant="caption" color="text.secondary" sx={styles.productComment}>
                              {product.comment}
                            </Typography>
                          )}
                        </Box>

                        {!isListClosed && (
                          <Box sx={styles.actionButtons}>
                            {!gestureMode && (
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditPanel(product)}
                                sx={styles.editButton}
                                aria-label={t('shoppingLists.editProduct')}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {!isTemplate && !gestureMode && (
                              <IconButton
                                size="small"
                                onClick={() => handleStatusChange(product.productId, 2)}
                                disabled={product.status === 2}
                                sx={styles.blockButton}
                                aria-label={t('shoppingLists.markUnavailable')}
                              >
                                <BlockIcon />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProduct(product.productId)}
                              sx={styles.deleteButton}
                              aria-label={t('shoppingLists.removeProduct')}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })
        )}
      </Paper>

      <Box sx={styles.addButtonContainer}>
        {list.status === 2 ? (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenCopyDialog}
                startIcon={<ContentCopyIcon />}
                sx={styles.addButton}
                fullWidth
              >
                {t('shoppingLists.copyList')}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleReopenList}
                startIcon={<RestoreIcon />}
                sx={styles.addButton}
                fullWidth
              >
                {t('shoppingLists.reopenList')}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, marginTop: 1 }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleOpenDeleteDialog}
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                sx={styles.addButton}
                fullWidth
              >
                {deleting ? t('common.deleting') : t('shoppingLists.deleteList')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleConvertToTemplate}
                startIcon={<BookmarkAddIcon />}
                sx={styles.addButton}
                fullWidth
              >
                {t('shoppingLists.useAsTemplate')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            {(list.status === 1 || list.status === 10) && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={handleOpenAddPanel}
                  fullWidth
                  sx={styles.addButton}
                >
                  {t('shoppingLists.addProduct')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlaylistAddIcon />}
                  onClick={handleOpenAddMultiplePanel}
                  fullWidth
                  sx={styles.addButton}
                >
                  {t('shoppingLists.addMultipleProducts')}
                </Button>
              </Box>
            )}
            {list.status === 10 ? (
              <Box sx={{ display: 'flex', gap: 1, marginTop: 1 }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleRemoveTemplate}
                  startIcon={<BookmarkRemoveIcon />}
                  sx={styles.addButton}
                  fullWidth
                >
                  {t('shoppingLists.removeTemplate')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleOpenCopyDialog}
                  startIcon={<PlaylistAddIcon />}
                  sx={styles.addButton}
                  fullWidth
                >
                  {t('shoppingLists.useTemplate')}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, marginTop: 1 }}>
                {/*<IconButton
                  onClick={() => setGestureMode(!gestureMode)}
                  color={gestureMode ? 'primary' : 'default'}
                  aria-label={t('shoppingLists.toggleGestureMode')}
                  sx={{ border: '1px solid', borderColor: gestureMode ? 'primary.main' : 'grey.400' }}
                >
                  <TouchAppIcon />
                </IconButton>*/}
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCompleteList}
                  startIcon={<CheckCircleIcon />}
                  fullWidth
                  sx={styles.addButton}
                >
                  {t('shoppingLists.completeList')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      <AddProductPanel
        open={addPanelOpen}
        onClose={handleCloseAddPanel}
        onAddProduct={handleAddProduct}
        usedProductIds={list.products.map((p) => p.productId)}
      />

      <AddMultipleProductsDialog
        open={addMultiplePanelOpen}
        onClose={handleCloseAddMultiplePanel}
        onAddProducts={handleAddMultipleProducts}
        usedProductIds={list.products.map((p) => p.productId)}
      />

      <EditProductPanel
        open={editPanelOpen}
        onClose={handleCloseEditPanel}
        product={editingProduct}
        onSave={handleSaveProductEdit}
      />

      <Drawer anchor="bottom" open={copyDialogOpen} onClose={handleCloseCopyDialog}>
        <Box sx={{ padding: 3, minHeight: '250px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="h6">
              {list.status === 10 ? t('shoppingLists.useTemplate') : t('shoppingLists.copying')}
            </Typography>
            <IconButton onClick={handleCloseCopyDialog} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box component="form" onSubmit={handleCopyList}>
            <TextField
              label={t('shoppingLists.listName')}
              value={copyListName}
              onChange={(e) => setCopyListName(e.target.value)}
              fullWidth
              required
              disabled={copying}
              autoFocus
              sx={{ marginBottom: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button onClick={handleCloseCopyDialog} disabled={copying}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={copying || !copyListName.trim()}
                startIcon={copying ? <CircularProgress size={20} /> : null}
              >
                {copying ? t('common.saving') : (list.status === 10 ? t('shoppingLists.useTemplate') : t('shoppingLists.copyList'))}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={t('shoppingLists.confirmDeleteTitle')}
        message={t('shoppingLists.confirmDeleteMessage')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteDialog}
      />
    </Box>
  );
};

export default ShoppingListView;